import React, { useEffect } from 'react';
import { FaMinus, FaPlus } from "react-icons/fa6";
import CountrySelectInput from "../shared/countrySelectInput";
import ValidityDate from "./helperComponent/validityDate";
import TransportationMode from "./TransportationMode";
import CIFValue from "./helperComponent/CIFValue";
import ServiceTemp from "./servicesTemp/tempAdminEstimate";
import InsuranceCheckbox from "./helperComponent/insuranceCheckBox";
import { AmountFormat } from "../../lib/helper/amountFormat";
import { Select } from "antd";
import DiscountInput from "./helperComponent/DiscountInput";

const NewQuoteForm = ({
    formData,
    setFormData,
    validationErrors,
    setValidationErrors,
    templateSelected,
    setTemplateSelected,
    attemptedSubmit,
    isPopupOpen,
    setIsPopupOpen,
    areMandatoryFieldsFilled,
    discountType = 'percentage',
    discountValue = '',
    onDiscountTypeChange,
    onDiscountValueChange,
}) => {
    const currencies = [
        { currency: "USD", symbol: "$" },
        { currency: "EUR", symbol: "€" },
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === "vatPercentage") {
            // Match Add Quote From Request behavior: optional, non-negative, coerce to number
            const numValue = value === "" ? "" : parseFloat(value);
            if (value === "" || (!isNaN(numValue) && numValue >= 0)) {
                setFormData(prevData => ({
                    ...prevData,
                    [name]: value === "" ? "" : numValue,
                }));
                setValidationErrors(prev => ({ ...prev, vatPercentage: false }));
            } else {
                setValidationErrors(prev => ({ ...prev, vatPercentage: true }));
            }
        } else {
            setFormData(prevData => ({
                ...prevData,
                [name]: value
            }));
        }
    };

    const handleCurrencyChange = (value) => {
        setFormData(prevData => ({
            ...prevData,
            currency: value,
            cargoValue: {
                ...prevData.cargoValue,
                currency: value
            },
            cifValue: {
                ...prevData.cifValue,
                currency: value
            }
        }));
    };

    const handlePcsOfPkgInput = (index, field, e) => {
        const { value } = e.target;
        if (value === "" || (/^\d*\.?\d*$/.test(value) && parseFloat(value) > 0)) {
            setFormData((prevData) => {
                const newData = { ...prevData };
                const pcsPackage = [...newData.pcsPackage];
                if (pcsPackage[index]) {
                    if (field === "quantity") {
                        pcsPackage[index][field] = value;
                    } else {
                        pcsPackage[index][field].value = value;
                    }
                }
                newData.pcsPackage = pcsPackage;
                return newData;
            });
        }
    };

    const handleUnitChange = (index, field, e) => {
        const { value } = e.target;
        setFormData((prevData) => {
            const newData = { ...prevData };
            const pcsPackage = [...newData.pcsPackage];
            if (pcsPackage[index]) {
                if (field === 'weight') {
                    pcsPackage[index].weight.unit = value;
                } else {
                    pcsPackage[index].length.unit = value;
                    pcsPackage[index].width.unit = value;
                    pcsPackage[index].height.unit = value;
                }
            }
            newData.pcsPackage = pcsPackage;
            return newData;
        });
    };

    const handleAddMorePcsOfpackage = () => {
        setFormData((prevData) => ({
            ...prevData,
            pcsPackage: [
                ...prevData.pcsPackage,
                {
                    quantity: "",
                    weight: { value: "", unit: "kg" },
                    length: { value: "", unit: "cm" },
                    width: { value: "", unit: "cm" },
                    height: { value: "", unit: "cm" },
                },
            ],
        }));
    };

    const handleRemovePcsOfpackage = () => {
        setFormData((prevData) => ({
            ...prevData,
            pcsPackage: prevData.pcsPackage.slice(0, -1),
        }));
    };

    // Keep financials + cargo/cif in sync in real-time as user edits services/VAT/insurance
    useEffect(() => {
        try {
            const subTotal = (formData.services || []).reduce((acc, service) => {
                const qty = parseFloat(service.numberOfItem || 1);
                const price = parseFloat(service.exclVAT || 0);
                return acc + qty * price;
            }, 0);

            const vatPct = parseFloat(formData.vatPercentage || 0) || 0;
            const vatAmount = vatPct === 0 ? 0 : (subTotal * vatPct) / 100;
            const insurance = parseFloat(formData.insurance || 0) || 0;
            const total = subTotal + vatAmount + insurance;

            setFormData(prev => ({
                ...prev,
                subTotal: subTotal.toFixed(2),
                vatAmount: vatAmount.toFixed(2),
                total: total.toFixed(2),
            }));
        } catch (e) {
            // no-op; keep UI resilient if services are mid-edit
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.services, formData.vatPercentage, formData.insurance]);

    // Check if currencies are aligned for Calculate Estimate
    const areCurrenciesAligned = () => {
        // For new customers, use the form's currency field as the reference (user currency)
        const userCurrency = formData.currency;
        if (!userCurrency) return false; // No user currency set

        return (
            userCurrency === formData.cargoValue.currency &&
            userCurrency === formData.cifValue.currency
        );
    };

    return (
        <div id="data">
            <h2 className="mx-[30px] font-bold text-xl mt-4">New Customer </h2>
            <div className="flex gap-5 items-start md:w-3/4 ">
                <div className="flex flex-col gap-1 w-1/4 py-2 px-8">
                    <label className="w-full text-sm font-normal" htmlFor="quoteNumber">
                        Quote Number <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        placeholder=""
                        className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                        id="quoteNumber"
                        onChange={handleInputChange}
                        value={formData.quoteNumber}
                        name="quoteNumber"
                        min={formData?.quoteNumber}
                    />
                </div>
                <ValidityDate formData={formData} setFormData={setFormData} />
            </div>

            <div className="mx-[30px] grid md:grid-cols-3 grid-cols-1 gap-3">
                <div className="flex flex-col gap-1 py-2">
                    <label className="w-full text-sm font-normal" htmlFor="name">
                        Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        placeholder="Name"
                        className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                        onChange={handleInputChange}
                        value={formData.name}
                        name="name"
                        id="name"
                    />
                </div>
                <div className="flex flex-col gap-1 py-2">
                    <label className="w-full text-sm font-normal" htmlFor="email">
                        Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="email"
                        placeholder="Email Address"
                        className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                        onChange={handleInputChange}
                        value={formData.email}
                        name="email"
                        id="email"
                        autoComplete="off"
                    />
                </div>
                <div className="flex flex-col gap-1 py-2">
                    <label className="w-full text-sm font-normal" htmlFor="addressone">
                        Postal Code <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        placeholder="Postal Code"
                        className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                        onChange={handleInputChange}
                        value={formData.postalCode}
                        name="postalCode"
                        id="postalCode"
                    />
                </div>
                <div className="flex flex-col gap-1 py-2">
                    <label className="w-full text-sm font-normal" htmlFor="addresst">
                        Address <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        placeholder="House No"
                        className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                        onChange={handleInputChange}
                        value={formData.address}
                        name="address"
                        id="address"
                    />
                </div>
                <div className="flex flex-col gap-1 py-2">
                    <label className="w-full text-sm font-normal" htmlFor="city">
                        User City <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        placeholder="Enter Your City "
                        className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                        onChange={handleInputChange}
                        value={formData.city}
                        name="city"
                        id="city"
                    />
                </div>
                <div className="flex flex-col gap-1 py-2">
                    <label className="w-full text-sm font-normal" htmlFor="country">
                        User Country <span className="text-red-500">*</span>
                    </label>
                    <CountrySelectInput
                        label=""
                        onChange={handleInputChange}
                        value={formData.country}
                        name="country"
                        id="country"
                    />
                </div>
            </div>

            {/* Hidden decoy inputs to prevent browser autofill from hijacking the Currency dropdown */}
            <div style={{ position: "absolute", opacity: 0, height: 0, width: 0, overflow: "hidden", zIndex: -1 }}>
                <input type="text" name="fake_username_autofill" tabIndex="-1" autoComplete="off" />
                <input type="password" name="fake_password_autofill" tabIndex="-1" autoComplete="new-password" />
            </div>

            {/* Currency Field - Added */}
            <div className="mx-[30px] grid md:grid-cols-2 grid-cols-1 gap-3">
                <div className="flex flex-col gap-1 py-2">
                    <label className="w-full text-sm font-normal" htmlFor="currency">
                        <span className="text-red-500">*</span> Currency
                    </label>
                    <Select
                        placeholder="Select Currency"
                        value={formData.currency}
                        onChange={handleCurrencyChange}
                        className="w-full h-8"
                        id="currency"
                    >
                        {currencies.map(({ currency, symbol }) => (
                            <Select.Option key={currency} value={currency}>
                                {symbol} {currency}
                            </Select.Option>
                        ))}
                    </Select>
                </div>

                {/* Password Field - Added */}
                <div className="flex flex-col gap-1 py-2">
                    <label className="w-full text-sm font-normal" htmlFor="password">
                        Password (Optional)
                    </label>
                    <input
                        type="password"
                        placeholder="Enter Password"
                        className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                        onChange={handleInputChange}
                        value={formData.password || ""}
                        name="password"
                        id="password"
                        autoComplete="new-password"
                        data-lpignore="true"
                    />
                </div>
            </div>

            <div className="mx-[30px] ">
                <div className="flex flex-col gap-1 py-2">
                    <label className="w-full text-sm font-normal" htmlFor="loadAddress">
                        Load Address <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        placeholder="Street 23, new town"
                        className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                        id="loadAddress"
                        onChange={handleInputChange}
                        value={formData.loadAddress}
                        name="loadAddress"
                    />
                </div>
            </div>
            <div className="mx-[30px] grid md:grid-cols-4 grid-cols-1 gap-3">
                <div className="flex flex-col gap-1 py-2">
                    <label className="w-full text-sm font-normal" htmlFor="loadAddress">
                        Load Country <span className="text-red-500">*</span>
                    </label>
                    <CountrySelectInput
                        label=""
                        onChange={handleInputChange}
                        value={formData.loadCountry}
                        name="loadCountry"
                        id="loadCountry"
                    />
                </div>
                <div className="flex flex-col gap-1 py-2">
                    <label className="w-full text-sm font-normal" htmlFor="loadAddress">
                        Load City <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        placeholder="city name"
                        className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                        id="loadCity"
                        onChange={handleInputChange}
                        value={formData.loadCity}
                        name="loadCity"
                    />
                </div>
                <div className="flex flex-col gap-1 py-2">
                    <label className="w-full text-sm font-normal" htmlFor="loadAddress">
                        Load Postal Code <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        placeholder="Postal Code"
                        className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                        name="loadPostalCode"
                        id="loadPostalCode"
                        onChange={handleInputChange}
                        value={formData.loadPostalCode}
                    />
                </div>
                <div className="flex flex-col gap-1 py-2">
                    <label className="w-full text-sm font-normal" htmlFor="loadAddress">
                        Load Company Name
                    </label>
                    <input
                        type="text"
                        placeholder="Load Company Name "
                        className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                        name="loadAddress_CompanyName"
                        id="loadAddress_CompanyName"
                        onChange={handleInputChange}
                        value={formData.loadAddress_CompanyName}
                    />
                </div>
            </div>

            <div className="mx-[30px] ">
                <div className="flex flex-col gap-1 py-2">
                    <label className="w-full text-sm font-normal" htmlFor="country">
                        Unload Address <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        placeholder="Street 23, new town"
                        className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                        id="unloadAddress"
                        onChange={handleInputChange}
                        value={formData.unloadAddress}
                        name="unloadAddress"
                    />
                </div>
            </div>

            <div className="mx-[30px] grid md:grid-cols-4 grid-cols-1 gap-3">
                <div className="flex flex-col gap-1 py-2">
                    <label className="w-full text-sm font-normal" htmlFor="loadAddress">
                        Unload country <span className="text-red-500">*</span>
                    </label>
                    <CountrySelectInput
                        label=""
                        onChange={handleInputChange}
                        value={formData.unloadCountry}
                        name="unloadCountry"
                        id="unloadCountry"
                    />
                </div>
                <div className="flex flex-col gap-1 py-2 ">
                    <label className="w-full text-sm font-normal" htmlFor="loadAddress">
                        Unload City <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        placeholder="select city"
                        className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                        id="unloadCity"
                        onChange={handleInputChange}
                        value={formData.unloadCity}
                        name="unloadCity"
                    />
                </div>
                <div className="flex flex-col gap-1 py-2">
                    <label className="w-full text-sm font-normal" htmlFor="loadAddress">
                        Unload Postal Code <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        placeholder="Postal Code"
                        className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                        name="unloadPostalCode"
                        id="unloadPostalCode"
                        onChange={handleInputChange}
                        value={formData.unloadPostalCode}
                    />
                </div>
                <div className="flex flex-col gap-1 py-2">
                    <label className="w-full text-sm font-normal" htmlFor="loadAddress">
                        Unload Company Name
                    </label>
                    <input
                        type="text"
                        placeholder="unload Company Name "
                        className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                        name="unloadAddress_CompanyName"
                        id="unloadAddress_CompanyName"
                        onChange={handleInputChange}
                        value={formData.unloadAddress_CompanyName}
                    />
                </div>
            </div>

            <div className="mx-[30px]">
                <div className="flex flex-col gap-1 py-2 ">
                    <label className="w-full text-sm font-normal" htmlFor="loadAddress">
                        Transportation Mode <span className="text-red-500">*</span>
                    </label>
                    <TransportationMode setFormData={setFormData} formData={formData} />
                </div>
            </div>

            <div className="mx-[30px] grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex flex-col gap-1 py-2">
                    <label className="w-full text-sm font-normal" htmlFor="cargoValue">
                        Cargo Value <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center">
                        <input
                            type="text"
                            className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                            name="cargoValue"
                            id="cargoValue"
                            onChange={(e) => {
                                const value = e.target.value;
                                if (
                                    value === "" ||
                                    (/^\d*\.?\d*$/.test(value) && parseFloat(value) > 0)
                                ) {
                                    setFormData((prevFormData) => ({
                                        ...prevFormData,
                                        cargoValue: {
                                            ...prevFormData.cargoValue,
                                            value: value,
                                        },
                                    }));
                                }
                            }}
                            value={formData.cargoValue.value || ""}
                        />
                        <Select
                            name="cargoValue-currency"
                            id="cargoValue-currency"
                            onChange={(e) => {
                                setFormData((prevFormData) => ({
                                    ...prevFormData,
                                    cargoValue: {
                                        ...prevFormData.cargoValue,
                                        currency: e,
                                    },
                                    cifValue: {
                                        ...prevFormData.cifValue,
                                        currency: e,
                                    },
                                }));
                            }}
                            value={formData.cargoValue.currency || ""}
                            options={[
                                { value: "USD", label: "USD" },
                                { value: "EUR", label: "EUR" },
                                { value: "GBP", label: "GBP" },
                            ]}
                            showSearch
                        />
                    </div>
                </div>

                <div className="flex flex-col gap-1 py-2">
                    <label className="w-full text-sm font-normal" htmlFor="cifValue">
                        CIF Value <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-1">
                        <CIFValue formData={formData} setFormData={setFormData} />
                        <Select
                            name="cifValue-currency"
                            id="cifValue-currency"
                            onChange={(e) => {
                                setFormData((prevFormData) => ({
                                    ...prevFormData,
                                    cargoValue: {
                                        ...prevFormData.cargoValue,
                                        currency: e,
                                    },
                                    cifValue: {
                                        ...prevFormData.cifValue,
                                        currency: e,
                                    },
                                }));
                            }}
                            value={formData.cifValue.currency || ""}
                            options={[
                                { value: "USD", label: "USD" },
                                { value: "EUR", label: "EUR" },
                                { value: "GBP", label: "GBP" },
                            ]}
                            showSearch
                        />
                    </div>
                </div>
            </div>

            <h2 className="mx-[30px] font-bold text-xl mt-4">Goods</h2>

            {formData.pcsPackage.map((formItem, index) => (
                <div
                    key={index}
                    className="mx-[30px] flex-wrap justify-between flex items-center gap-5"
                >
                    <div className="w-full lg:w-[10%] flex flex-col gap-1 py-2">
                        <label className="w-full text-sm font-normal" htmlFor={`amount-${index}`}>
                            Quantity <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="number"
                            className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                            id={`quantity-${index}`}
                            onChange={(e) => handlePcsOfPkgInput(index, "quantity", e)}
                            onKeyDown={(e) => {
                                if (e.key === "e" || e.key === "-" || e.key === "+") {
                                    e.preventDefault();
                                }
                            }}
                            min="1"
                            step="1"
                            value={formData.pcsPackage[index]?.quantity || ""}
                        />
                    </div>
                    <div className="flex w-full lg:w-1/5 flex-col gap-1 py-2">
                        <label className="w-full text-sm font-normal" htmlFor={`weight-${index}`}>
                            Weight<span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center">
                            <input
                                type="number"
                                className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                name={`weight-${index}`}
                                id={`weight-${index}`}
                                onChange={(e) => handlePcsOfPkgInput(index, "weight", e)}
                                onKeyDown={(e) => {
                                    if (e.key === "e" || e.key === "-" || e.key === "+") {
                                        e.preventDefault();
                                    }
                                }}
                                min="1"
                                step="1"
                                value={formData.pcsPackage[index]?.weight.value || ""}
                            />
                            <select
                                className="ml-2 p-1 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                name={`weight-unit-${index}`}
                                id={`weight-unit-${index}`}
                                onChange={(e) => handleUnitChange(index, "weight", e)}
                                value={formData.pcsPackage[index]?.weight.unit || ""}
                            >
                                <option value="kg">kg</option>
                                <option value="lbs">lbs</option>
                            </select>
                        </div>
                    </div>
                    <div className="flex w-full lg:w-1/5 flex-col gap-1 py-2">
                        <label className="w-full text-sm font-normal" htmlFor={`length-${index}`}>
                            Length<span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center">
                            <input
                                type="number"
                                className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                name={`length-${index}`}
                                id={`length-${index}`}
                                onChange={(e) => handlePcsOfPkgInput(index, "length", e)}
                                onKeyDown={(e) => {
                                    if (e.key === "e" || e.key === "-" || e.key === "+") {
                                        e.preventDefault();
                                    }
                                }}
                                min="1"
                                step="1"
                                value={formData.pcsPackage[index]?.length.value || ""}
                            />
                            <select
                                className="ml-2 p-1 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                name={`length-unit-${index}`}
                                id={`length-unit-${index}`}
                                onChange={(e) => handleUnitChange(index, "length", e)}
                                value={formData.pcsPackage[index]?.length.unit || ""}
                            >
                                <option value="cm">cm</option>
                                <option value="in">in</option>
                            </select>
                        </div>
                    </div>
                    <div className="flex w-full lg:w-1/5 flex-col gap-1 py-2">
                        <label className="w-full text-sm font-normal" htmlFor={`width-${index}`}>
                            Width<span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center">
                            <input
                                type="number"
                                className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                name={`width-${index}`}
                                id={`width-${index}`}
                                onChange={(e) => handlePcsOfPkgInput(index, "width", e)}
                                onKeyDown={(e) => {
                                    if (e.key === "e" || e.key === "-" || e.key === "+") {
                                        e.preventDefault();
                                    }
                                }}
                                min="1"
                                step="1"
                                value={formData.pcsPackage[index]?.width.value || ""}
                            />
                            <select
                                className="ml-2 p-1 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                name={`width-unit-${index}`}
                                id={`width-unit-${index}`}
                                onChange={(e) => handleUnitChange(index, "width", e)}
                                value={formData.pcsPackage[index]?.width.unit || ""}
                            >
                                <option value="cm">cm</option>
                                <option value="in">in</option>
                            </select>
                        </div>
                    </div>
                    <div className="flex w-full lg:w-1/5 flex-col gap-1 py-2">
                        <label className="w-full text-sm font-normal" htmlFor={`height-${index}`}>
                            Height<span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center">
                            <input
                                type="number"
                                className="w-full h-8 p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                name={`height-${index}`}
                                id={`height-${index}`}
                                onChange={(e) => handlePcsOfPkgInput(index, "height", e)}
                                onKeyDown={(e) => {
                                    if (e.key === "e" || e.key === "-" || e.key === "+") {
                                        e.preventDefault();
                                    }
                                }}
                                min="1"
                                step="1"
                                value={formData.pcsPackage[index]?.height.value || ""}
                            />
                            <select
                                className="ml-2 p-1 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                                name={`height-unit-${index}`}
                                id={`height-unit-${index}`}
                                onChange={(e) => handleUnitChange(index, "height", e)}
                                value={formData.pcsPackage[index]?.height.unit || ""}
                            >
                                <option value="cm">cm</option>
                                <option value="in">in</option>
                            </select>
                        </div>
                    </div>
                </div>
            ))}

            <div className="flex justify-between gap-5 items-center px-5 py-2">
                <div className="flex gap-1 items-center">
                    <div className="mx-[20px] mt-" onClick={handleAddMorePcsOfpackage}>
                        <FaPlus className="text-[20px] text-[#03336E] " />
                    </div>
                    <div className="mx-[5px]" onClick={handleRemovePcsOfpackage}>
                        <FaMinus className="text-[20px] text-[#03336E] " />
                    </div>
                </div>
                <div className="px-5 flex items-center gap-5">
                    <p className="text-[14px]"> Total Weight:</p>
                    <p className="text-[14px] font-extrabold">
                        {formData.totalWeight ? formData.totalWeight : "00"}
                    </p>
                </div>
            </div>

            <div className="mx-[30px] flex flex-col gap-1 py-2">
                <label className="w-full text-sm font-normal" htmlFor="country">
                    Description<span className="text-[red] ">*</span>
                </label>
                <textarea
                    placeholder="Description"
                    className="w-full h-[150px] p-2 text-sm border rounded-md outline-none active:border-cus-blue focus:border-cus-blue"
                    name="description"
                    id="description"
                    onChange={handleInputChange}
                    value={formData.description}
                    maxLength={1000}
                />
            </div>

            <ServiceTemp
                formData={formData}
                setFormData={setFormData}
                validationErrors={validationErrors}
                setValidationErrors={setValidationErrors}
                templateSelected={templateSelected}
                setTemplateSelected={setTemplateSelected}
                attemptedSubmit={attemptedSubmit}
                isPopupOpen={isPopupOpen}
                setIsPopupOpen={setIsPopupOpen}
                areMandatoryFieldsFilled={areMandatoryFieldsFilled}
                areCurrenciesAligned={areCurrenciesAligned}
            />

            <div className="mx-[30px] flex flex-col lg:flex-row gap-2 ">
                <div className="w-full lg:w-[60%] bg-[#FBFCFF] p-4 rounded-lg border border-cus-blue ">
                    <div className="flex items-center w-full lg:w-1/2 gap-1 py-2">
                        <label className="w-full text-base font-medium">
                            VAT Percentage :
                        </label>
                        <input
                            type="number"
                            className={`w-full p-2 text-sm border-2 rounded-md outline-none active:border-cus-blue focus:border-cus-blue ${validationErrors.vatPercentage ? "border-red-500" : ""}`}
                            name="vatPercentage"
                            id="vatPercentage"
                            onChange={handleInputChange}
                            onKeyDown={(e) => {
                                if (e.key === "e" || e.key === "-" || e.key === "+") {
                                    e.preventDefault();
                                }
                            }}
                            value={formData.vatPercentage || ""}
                            min="0"
                            step="1"
                        />
                    </div>
                    {validationErrors.vatPercentage && (
                        <p className="text-red-500 text-sm">
                            VAT Percentage must be greater than or equal to 0
                        </p>
                    )}
                    {/* <InsuranceCheckbox formData={formData} setFormData={setFormData} /> */}
                </div>

                <div className="w-full lg:w-[40%] bg-[#FBFCFF] p-4 border rounded-lg border-cus-blue">
                    <div className="flex justify-between">
                        <div>
                            <p className="text-[14px]">Sub Total</p>
                        </div>
                        <div className="">
                            <p className="text-[14px] font-extrabold">
                                {AmountFormat(formData.subTotal, formData.currency)}
                            </p>
                        </div>
                    </div>

                    {onDiscountTypeChange && onDiscountValueChange && (
                        <DiscountInput
                            discountType={discountType}
                            discountValue={discountValue}
                            onDiscountTypeChange={onDiscountTypeChange}
                            onDiscountValueChange={onDiscountValueChange}
                            currency={formData.currency}
                            discountAmount={formData.discount?.amount || 0}
                        />
                    )}

                    {formData.discount && formData.discount.amount > 0 && (
                        <div className="flex justify-between mt-1 text-orange-600">
                            <div>
                                <p className="text-[14px] font-medium">
                                    Discount ({formData.discount.type === 'percentage' ? `${Math.abs(formData.discount.value)}%` : 'Fixed'})
                                </p>
                            </div>
                            <div>
                                <p className="text-[14px] font-extrabold">
                                    - {AmountFormat(formData.discount.amount, formData.currency)}
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-between mt-1 ">
                        <div>
                            <p className="text-[14px]">Total VAT</p>
                        </div>
                        <div className="">
                            <p className="text-[14px] font-extrabold">
                                {AmountFormat(formData.vatAmount, formData.currency)}
                            </p>
                        </div>
                    </div>
                    <div className="flex justify-between mt-4 border-t border-cus-blue ">
                        <div className="mt-2">
                            <p className="text-[14px]">Total</p>
                        </div>
                        <div className="mt-2">
                            <p className="text-[14px] font-extrabold">
                                {AmountFormat(formData.total, formData.currency)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewQuoteForm;